import { useContext, useMemo, useCallback } from 'react';
import { ThemeContext } from 'grommet';
import { useSelector, useDispatch } from 'react-redux';
import { useToast } from 'granite-admin/core/components/Toast';
import { ConfigContext } from 'granite-admin/core/components/ConfigProvider';
import { getGrandEmitter } from 'granite-admin/utils/grandEmitter';
import { updateThemeConfigs, defaultState as defaultStateDucks, initialState } from 'granite-admin/common/ducks/themeConfigs';
import themeAPI from 'granite-admin/common/gateways/theme-api';
const defaultState = defaultStateDucks.themeConfigs;
function useThemeConfigs() {
  const {
    errorToast,
    successToast,
    infoToast
  } = useToast();
  const defaultConfig = useContext(ConfigContext);
  const defaultTheme = useContext(ThemeContext);
  const grandEmitter = useMemo(() => getGrandEmitter(), []);
  const dispatch = useDispatch();
  const themeConfigs = useSelector(({
    common
  }) => common.themeConfigs.themeConfigs);
  const getThemeConfigs = () => {
    const {
      theme: {
        theme = {},
        configs = {}
      } = {
        theme: {},
        configs: {}
      },
      pk,
      logo,
      favicon
    } = themeConfigs || defaultState;
    return {
      theme: Object.keys(theme).length > 0 ? theme : defaultTheme,
      configs: Object.keys(configs).length > 0 ? configs : defaultConfig,
      logo: logo !== '' ? logo : defaultState.logo,
      favicon: favicon !== '' ? favicon : defaultState.favicon,
      pk
    };
  };
  const getSavedThemeConfigs = useCallback(async () => {
    try {
      const data = await themeAPI.getLogoTheme();
      const theme = !data || !data.pk // in case theme is not saved for current user
      ? defaultState : {
        //in case theme is saved for current user
        ...defaultState,
        pk: data.pk,
        ...(data.logo ? {
          logo: data.logo
        } : {}),
        ...(data.favicon ? {
          favicon: data.favicon
        } : {}),
        ...(Object.keys(data.theme).length ? {
          theme: data.theme
        } : {})
      }; // create theme object from API data, use default logo, favicon & theme when not present in API data.
      dispatch(updateThemeConfigs(theme));
      if (theme.favicon) document.querySelector("link[rel='shortcut icon']").href = theme.favicon;else document.querySelector("link[rel='shortcut icon']").href = defaultState.favicon;
      grandEmitter.emit('UPDATE_THEME_CONFIGS', theme.theme);
    } catch (e) {
      errorToast(e?.errors?.title || 'Failed to get theme and configurations!');
    }
  }, [dispatch, errorToast, grandEmitter]);
  const saveThemeConfigs = async data => {
    try {
      const payload = {
        pk: data.pk,
        logo: data.logo,
        favicon: data.favicon,
        theme: {
          theme: data.theme,
          configs: data.configs
        }
      };
      await themeAPI.saveLogoTheme(payload);
      getSavedThemeConfigs();
      successToast('Theme and configurations are updated successfully!');
    } catch (e) {
      errorToast(e?.errors?.title || 'Failed to save theme and configurations!');
    }
  };
  const deleteThemeConfigs = async themePk => {
    try {
      if (themePk) {
        await themeAPI.deleteLogoTheme(themePk);
        getSavedThemeConfigs();
        successToast('Default theme and configurations are applied successfully!');
      } else {
        infoToast('Default theme and configurations are already applied!');
      }
    } catch (e) {
      errorToast(e?.errors?.title || 'Failed to set the default theme and configurations!');
    }
  };
  return {
    getThemeConfigs,
    getSavedThemeConfigs,
    saveThemeConfigs,
    deleteThemeConfigs,
    defaultThemeConfigs: initialState
  };
}
export default useThemeConfigs;